import {
  Company,
  CompanyUsersRelation,
  CompanyUsersStatus,
  CompanyUserWithCompanyAndRole,
  CompanyUserWithRoleAndPermissions,
  CompanyUserWithUserAndRole
} from '@shared/types/companies.types'
import { Role } from '@shared/types/roles.types'
import { useQuery } from '@tanstack/react-query'
import axiosClient from '../useAxios'

export const useUserCompanies = (
  userId: string,
  status?: CompanyUsersStatus
) => {
  return useQuery({
    queryKey: ['users', userId, 'companies', status],
    queryFn: async (): Promise<CompanyUserWithCompanyAndRole[]> => {
      const response = await axiosClient.get(
        `/users/${userId}/companies${status ? `?status=${status}` : ''}`
      )
      return response.data.data.userCompanies
    }
  })
}

export const useReceivedInvitations = (userId: string) => {
  return useQuery({
    queryKey: ['users', userId, 'companies', CompanyUsersStatus.PENDING_USER],
    queryFn: async (): Promise<CompanyUserWithCompanyAndRole[]> => {
      const response = await axiosClient.get(
        `/users/${userId}/companies?status=${CompanyUsersStatus.PENDING_USER}`
      )
      return response.data.data.userCompanies
    }
  })
}

export const useSentInvitations = (userId: string) => {
  return useQuery({
    queryKey: [
      'users',
      userId,
      'companies',
      CompanyUsersStatus.PENDING_COMPANY
    ],
    queryFn: async (): Promise<CompanyUserWithCompanyAndRole[]> => {
      const response = await axiosClient.get(
        `/users/${userId}/companies?status=${CompanyUsersStatus.PENDING_COMPANY}`
      )
      return response.data.data.userCompanies
    }
  })
}

export const useCompaniesDirectory = () => {
  return useQuery({
    queryKey: ['companies', 'not-associated'],
    queryFn: async (): Promise<
      (Company & { status: CompanyUsersStatus | null })[]
    > => {
      const params = new URLSearchParams()
      params.set('associated', 'false')

      const response = await axiosClient.get(`/companies?${params}`)
      return response.data.data.companies
    }
  })
}

export const useCompanyUsers = (
  companyId: string,
  queryParams?: {
    status?: CompanyUsersStatus
    relation?: CompanyUsersRelation
    role?: string
  }
) => {
  return useQuery({
    queryKey: ['companies', companyId, 'users', queryParams],
    queryFn: async (): Promise<CompanyUserWithUserAndRole[]> => {
      const searchParams = new URLSearchParams()

      if (queryParams?.status) {
        searchParams.append('status', queryParams.status)
      }
      if (queryParams?.relation) {
        searchParams.append('relation', queryParams.relation)
      }
      if (queryParams?.role) {
        searchParams.append('role', queryParams.role)
      }

      const queryString = searchParams.toString()
      const url = `/companies/${companyId}/users${queryString ? `?${queryString}` : ''}`

      const response = await axiosClient.get(url)
      return response.data.data.companyUsers
    }
  })
}

export const useCompany = (companyId: string) => {
  return useQuery({
    queryKey: ['companies', companyId],
    queryFn: async (): Promise<Company> => {
      const response = await axiosClient.get(`/companies/${companyId}`)
      return response.data.data.company
    },
    staleTime: 0
  })
}

export const useCompanyUser = (companyId: string, userId: string) => {
  return useQuery({
    queryKey: ['companies', companyId, 'users', userId],
    queryFn: async (): Promise<CompanyUserWithRoleAndPermissions> => {
      const response = await axiosClient.get(
        `/companies/${companyId}/users/${userId}`
      )
      return response.data.data.user
    },
    staleTime: 0
  })
}

export const useCompanyRoles = (companyId: string) => {
  return useQuery({
    queryKey: ['companies', companyId, 'roles'],
    queryFn: async (): Promise<Role[]> => {
      const response = await axiosClient.get(`/companies/${companyId}/roles`)
      return response.data.data.roles
    }
  })
}
